import { useState, useEffect, useMemo } from "react";
import {
    TablePagination,
    Button,
    Box,
    FormControl,
    FormControlLabel,
    InputAdornment,
    OutlinedInput,
    Switch,
    Typography,
} from "@mui/material";

import Table from "../../components/Table";
import ActionsMenu from "../../components/ActionsMenu";
import ClassroomModal from "./components/ClassroomModal";
import { ClassroomColumns } from "./components/ClassroomColumns";
import AddIcon from "@mui/icons-material/Add";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";

import api from "../../utils/api";

export default function Classrooms() {
    const columns = useMemo(() => ClassroomColumns(), []);

    const [search, setSearch] = useState("");
    const [showArchived, setShowArchived] = useState(false);
    const [pagination, setPagination] = useState({ page: 1, perPage: 40, total: 0, totalPages: 0 });
    const [activeFilters, setActiveFilters] = useState({});
    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState([]);
    const [row, setRow] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const fetchRows = async (filters = activeFilters, options = {}) => {
        const { resetPage = false, overridePagination = {} } = options;

        const currentPage = resetPage ? 1 : overridePagination.page ?? pagination.page;
        const currentPerPage = overridePagination.perPage ?? pagination.perPage;

        setLoading(true);

        try {
            const response = await api.get("admin/classrooms", {
                params: {
                    page: currentPage,
                    per_page: currentPerPage,
                    include_deleted: filters.showArchived ? true : false,
                    ...(filters.search ? { search: filters.search } : {}),
                },
            });

            const payload = response.data;
            setPagination({
                page: payload.current_page ?? currentPage,
                perPage: Number(payload.per_page ?? currentPerPage),
                total: payload.total ?? 0,
                totalPages: payload.last_page ?? 0,
            });

            setRows(payload.data || []);
        } catch (error) {
            console.error("Fetch failed", error);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteRows = async (ids) => {
        try {
            for (const rowId of ids) {
                await api.delete(`admin/classrooms/${rowId}`);
            }
            fetchRows(activeFilters);
        } catch (err) {
            console.error(err);
            alert(err?.response?.data?.message || "Failed to delete classroom.");
        }
    };

    const handleRestoreRow = async (rowId) => {
        try {
            await api.post(`admin/classrooms/${rowId}/restore`);
            fetchRows(activeFilters);
        } catch (err) {
            console.error(err);
            alert(err?.response?.data?.message || "Failed to restore classroom.");
        }
    };

    const handleOpenManageRow = (r) => {
        setRow(r ?? null);
        setIsModalOpen(true);
    };

    const handleCloseManageRow = (refetch) => {
        setRow(null);
        setIsModalOpen(false);
        if (refetch) {
            fetchRows();
        }
    };

    const handlePageChange = (newPage) => {
        fetchRows(activeFilters, { overridePagination: { page: newPage + 1 } });
    };

    useEffect(() => {
        const delayDebounce = setTimeout(() => {
            const newFilters = { search, showArchived };
            setActiveFilters(newFilters);
            fetchRows(newFilters, { resetPage: true });
        }, 500);
        return () => clearTimeout(delayDebounce);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search, showArchived]);

    if (loading && rows.length === 0) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography>Loading classrooms…</Typography>
            </Box>
        );
    }

    return (
        <Box>
            <Typography component="h2" variant="h6" sx={{ mb: 2 }}>
                Classrooms
            </Typography>
            <Box>
                <Box sx={{ display: "flex", mb: 2, justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                        <FormControl sx={{ width: { xs: "100%", md: "45ch" } }} variant="outlined">
                            <OutlinedInput
                                size="small"
                                id="classrooms-search"
                                placeholder="Search by name, teacher, student, lesson type, ID…"
                                sx={{ flexGrow: 1, background: "#fff" }}
                                onChange={(e) => setSearch(e.target.value)}
                                value={search}
                                startAdornment={
                                    <InputAdornment position="start" sx={{ color: "text.primary" }}>
                                        <SearchRoundedIcon fontSize="small" />
                                    </InputAdornment>
                                }
                                inputProps={{
                                    "aria-label": "search classrooms",
                                }}
                            />
                        </FormControl>
                        <FormControlLabel
                            sx={{ m: 0 }}
                            control={
                                <Switch
                                    size="small"
                                    checked={showArchived}
                                    onChange={(e) => setShowArchived(e.target.checked)}
                                />
                            }
                            label="Show archived"
                        />
                    </Box>
                    <Box sx={{ display: "flex" }}>
                        <Button
                            variant="contained"
                            size="small"
                            startIcon={<AddIcon color="success" />}
                            onClick={() => handleOpenManageRow()}
                        >
                            Create New
                        </Button>
                    </Box>
                </Box>

                <Table
                    loading={loading}
                    rows={rows}
                    columns={columns}
                    selactable={false}
                    draggable={false}
                    renderActions={(r) => {
                        const name = r.classroom_name || r.name || `Classroom #${r.id}`;
                        const isDeleted = !!r.deleted_at;
                        const actions = isDeleted
                            ? [
                                  {
                                      label: "Restore",
                                      onClick: () => handleRestoreRow(r.id),
                                      confirm: true,
                                      confirmText: `Restore classroom "${name}"?`,
                                  },
                              ]
                            : [
                                  { label: "Edit", onClick: () => handleOpenManageRow(r) },
                                  {
                                      label: "Delete",
                                      onClick: () => handleDeleteRows([r.id]),
                                      confirm: true,
                                      confirmText: `Are you sure you want to delete classroom "${name}"?`,
                                  },
                              ];
                        return <ActionsMenu actions={actions} />;
                    }}
                />

                <TablePagination
                    component="div"
                    count={pagination.total}
                    page={pagination.page - 1}
                    rowsPerPage={pagination.perPage}
                    onPageChange={(event, newPage) => {
                        handlePageChange(newPage);
                    }}
                    rowsPerPageOptions={[]}
                />
            </Box>

            <ClassroomModal row={row} open={isModalOpen} onClose={handleCloseManageRow} />
        </Box>
    );
}
